import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { supabase } from '../supabaseClient'
import { showToast } from '../Toast'
import { useWorkspace } from '../contexts/WorkspaceContext'

export default function ConsultaSerasa() {
  const { saldo } = useWorkspace()
  const [cpfCnpj, setCpfCnpj] = useState('')
  const [loading, setLoading] = useState(false)
  const [etapa, setEtapa] = useState('consulta') // consulta, pagamento, resultado
  const [custoBase] = useState(16.00)
  const [margemSistema] = useState(4.00)
  const custoConsulta = parseFloat((custoBase + margemSistema).toFixed(2))
  const [resultado, setResultado] = useState(null)
  const [pixCopiaCola, setPixCopiaCola] = useState('')
  const [pagamentoId, setPagamentoId] = useState(null)
  const [verificandoPagamento, setVerificandoPagamento] = useState(false)

  const formatarCpfCnpj = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '')
    if (apenasNumeros.length <= 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
    return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  const handleCpfChange = (e) => {
    const valor = e.target.value.replace(/\D/g, '')
    if (valor.length <= 14) {
      setCpfCnpj(formatarCpfCnpj(valor))
    }
  }

  const verificarSaldoEProceder = async () => {
    if (!cpfCnpj || cpfCnpj.length < 11) {
      showToast('Digite um CPF ou CNPJ válido', 'error')
      return
    }

    setLoading(true)
    try {
      // Verifica se tem saldo suficiente
      if (saldo?.saldo_disponivel >= custoConsulta) {
        if (window.confirm(`Deseja realizar a consulta por R$ ${custoConsulta.toFixed(2)}? O valor será descontado do seu saldo.`)) {
          await realizarConsulta('saldo')
        }
      } else {
        // Gera PIX
        setEtapa('pagamento')
        await gerarPixPagamento()
      }
    } catch (error) {
      console.error(error)
      showToast('Erro ao processar solicitação', 'error')
    } finally {
      setLoading(false)
    }
  }

  const gerarPixPagamento = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.functions.invoke('asaas-serasa-pay', {
        body: { 
          metodo: 'pix',
          valor_total: custoConsulta,
          custo_base_assas: custoBase,
          margem_sistema: margemSistema,
          split: { asaas: custoBase, sistema: margemSistema }
        }
      })

      if (error) throw error
      
      setPixCopiaCola(data.pixCopiaCola)
      setPagamentoId(data.paymentId)
      iniciarVerificacaoPagamento(data.paymentId)
    } catch (error) {
      showToast('Erro ao gerar PIX: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const iniciarVerificacaoPagamento = (id) => {
    setVerificandoPagamento(true)
    const interval = setInterval(async () => {
      const { data } = await supabase.functions.invoke('asaas-check-payment', {
        body: { paymentId: id }
      })
      
      if (data?.status === 'RECEIVED' || data?.status === 'CONFIRMED') {
        clearInterval(interval)
        setVerificandoPagamento(false)
        await realizarConsulta('pix')
      }
    }, 5000)

    // Timeout de 5 min
    setTimeout(() => {
      clearInterval(interval)
      setVerificandoPagamento(false)
    }, 300000)
  }

  const realizarConsulta = async (metodoPagamento) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.functions.invoke('asaas-serasa-consult', {
        body: { 
          cpfCnpj: cpfCnpj.replace(/\D/g, ''),
          metodoPagamento,
          valor_total: custoConsulta,
          custo_base_assas: custoBase,
          margem_sistema: margemSistema,
          split: { asaas: custoBase, sistema: margemSistema }
        }
      })

      if (error) throw error
      
      let pdfUrl = null
      if (data?.reportFile) {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const base64 = data.reportFile
          const byteCharacters = atob(base64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          const path = `${user.id}/${data.id}.pdf`
          await supabase.storage.from('serasa').upload(path, blob, { upsert: true })
          const { data: pub } = supabase.storage.from('serasa').getPublicUrl(path)
          pdfUrl = pub?.publicUrl || null
        }
      }

      setResultado({ ...data, pdfUrl })
      setEtapa('resultado')
      showToast('Consulta realizada com sucesso!', 'success')
    } catch (error) {
      showToast('Erro ao consultar: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const novaConsulta = () => {
    setCpfCnpj('')
    setEtapa('consulta')
    setResultado(null)
    setPixCopiaCola('')
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#101828', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon icon="ph:magnifying-glass" width="28" />
          Consulta Serasa
        </h1>
        <p style={{ color: '#667085', marginTop: '8px', maxWidth: '700px' }}>
          Consultar a saúde financeira dos seus atuais e futuros clientes trará mais segurança para seu negócio e aumentará as chances do seu serviço ou produto serem pagos.
        </p>
      </div>

      {etapa === 'consulta' && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '16px', 
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          maxWidth: '800px', // Aumentado de 600px
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            backgroundColor: '#ecfdf5', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <Icon icon="ph:shield-check" width="32" color="#12b76a" />
          </div>

          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#101828', marginBottom: '8px' }}>
            Consulte CPF ou CNPJ
          </h2>
          <p style={{ color: '#667085', marginBottom: '32px' }}>
            Tenha acesso a restrições, score e protestos.
          </p>

          <div style={{ marginBottom: '24px', textAlign: 'left' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#344054', marginBottom: '6px' }}>
              Documento (CPF ou CNPJ)
            </label>
            <input
              type="text"
              value={cpfCnpj}
              onChange={handleCpfChange}
              placeholder="000.000.000-00"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #d0d5dd',
                fontSize: '16px',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)'
              }}
            />
          </div>

          <div style={{ 
            backgroundColor: '#f9fafb', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#475467', fontWeight: '500' }}>Custo da consulta</span>
            <span style={{ color: '#101828', fontWeight: '700', fontSize: '18px' }}>R$ {custoConsulta.toFixed(2).replace('.', ',')}</span>
          </div>

          <button
            onClick={verificarSaldoEProceder}
            disabled={loading || !cpfCnpj}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#12b76a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading || !cpfCnpj ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading || !cpfCnpj ? 0.7 : 1
            }}
          >
            {loading ? 'Processando...' : 'Consultar Agora'}
          </button>
        </div>
      )}

      {etapa === 'pagamento' && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '16px', 
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          maxWidth: '800px', // Aumentado de 500px para manter padrão
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#101828', marginBottom: '24px' }}>
            Pagamento via PIX
          </h2>
          
          <div style={{ marginBottom: '24px' }}>
             {/* QR Code */}
             <div style={{ 
               width: '200px', 
               height: '200px', 
               backgroundColor: '#f2f4f7', 
               margin: '0 auto',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               borderRadius: '8px',
               border: '1px solid #eaecf0'
             }}>
               {pixCopiaCola ? (
                 <img 
                   src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixCopiaCola)}`} 
                   alt="QR Code PIX" 
                   style={{ width: '100%', height: '100%', borderRadius: '7px' }}
                 />
               ) : (
                 <Icon icon="eos-icons:loading" width="40" color="#98a2b3" />
               )}
             </div>
          </div>

          <div style={{ marginBottom: '32px', maxWidth: '400px', margin: '0 auto 32px auto' }}>
            <p style={{ fontSize: '14px', color: '#344054', marginBottom: '8px', fontWeight: '500', textAlign: 'left' }}>Pix Copia e Cola</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                value={pixCopiaCola} 
                readOnly 
                style={{ 
                  flex: 1, 
                  padding: '10px 14px', 
                  borderRadius: '8px', 
                  border: '1px solid #d0d5dd',
                  fontSize: '14px',
                  backgroundColor: '#f9fafb',
                  color: '#667085',
                  outline: 'none'
                }} 
              />
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(pixCopiaCola)
                  showToast('Copiado!', 'success')
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d0d5dd',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500',
                  color: '#344054',
                  transition: 'all 0.2s'
                }}
              >
                <Icon icon="ph:copy" width="18" />
                Copiar
              </button>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '10px', 
            color: '#027a48', 
            backgroundColor: '#ecfdf3', 
            padding: '12px', 
            borderRadius: '8px',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <Icon icon="eos-icons:loading" width="20" />
            <span style={{ fontSize: '14px', fontWeight: '600' }}>Aguardando pagamento...</span>
          </div>

          <button
            onClick={() => setEtapa('consulta')}
            style={{
              marginTop: '24px',
              background: 'none',
              border: 'none',
              color: '#667085',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              margin: '24px auto 0 auto',
              padding: '8px 16px',
              borderRadius: '6px'
            }}
          >
            <Icon icon="ph:x" /> Cancelar
          </button>
        </div>
      )}

      {etapa === 'resultado' && resultado && (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ marginBottom: '24px' }}>
            <button 
              onClick={novaConsulta}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                background: 'none', 
                border: 'none', 
                color: '#667085',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <Icon icon="ph:arrow-left" /> Nova Consulta
            </button>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            padding: '32px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', borderBottom: '1px solid #eaecf0', paddingBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#101828', margin: 0 }}>
                  Relatório de Consulta
                </h2>
                <p style={{ color: '#667085', marginTop: '4px' }}>{resultado.nome || 'Nome não informado'}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', color: '#667085' }}>Protocolo</div>
                <div style={{ fontWeight: '600', color: '#101828' }}>{resultado.protocolo || '---'}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#667085', marginBottom: '4px' }}>CPF/CNPJ</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#101828' }}>{cpfCnpj}</div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#667085', marginBottom: '4px' }}>Situação Cadastral</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: resultado.situacao === 'REGULAR' ? '#12b76a' : '#f04438' }}>
                  {resultado.situacao || '---'}
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#667085', marginBottom: '4px' }}>Score</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#12b76a' }}>{resultado.score || '---'}</div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#101828', marginBottom: '12px' }}>
                Relatório PDF
              </h3>
              {resultado.pdfUrl ? (
                <a href={resultado.pdfUrl} target="_blank" rel="noreferrer" style={{ 
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  backgroundColor: '#111827', color: 'white', padding: '10px 14px',
                  borderRadius: '8px', textDecoration: 'none', fontSize: '14px'
                }}>
                  <Icon icon="mdi:file-pdf-box" /> Abrir PDF
                </a>
              ) : resultado.downloadUrl ? (
                <a href={resultado.downloadUrl} target="_blank" rel="noreferrer" style={{ 
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  backgroundColor: '#0ea5e9', color: 'white', padding: '10px 14px',
                  borderRadius: '8px', textDecoration: 'none', fontSize: '14px'
                }}>
                  <Icon icon="mdi:download" /> Baixar do Asaas
                </a>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#fcfcfd', border: '1px dashed #eaecf0', borderRadius: '8px', color: '#667085', fontSize: '13px' }}>
                  PDF indisponível
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#101828', marginBottom: '16px' }}>
                Restrições e Pendências
              </h3>
              {resultado.restricoes && resultado.restricoes.length > 0 ? (
                <div style={{ border: '1px solid #eaecf0', borderRadius: '8px', overflow: 'hidden' }}>
                  {resultado.restricoes.map((restricao, index) => (
                    <div key={index} style={{ padding: '16px', borderBottom: '1px solid #eaecf0', display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#101828' }}>{restricao.tipo}</div>
                        <div style={{ fontSize: '13px', color: '#667085' }}>{restricao.origem}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600', color: '#f04438' }}>R$ {restricao.valor}</div>
                        <div style={{ fontSize: '13px', color: '#667085' }}>{restricao.data}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#fcfcfd', borderRadius: '8px', border: '1px dashed #eaecf0' }}>
                  <Icon icon="ph:check-circle" width="32" color="#12b76a" style={{ marginBottom: '8px' }} />
                  <p style={{ color: '#667085', fontSize: '14px' }}>Nenhuma restrição encontrada.</p>
                </div>
              )}
            </div>

            <div style={{ marginTop: '32px', textAlign: 'center', fontSize: '12px', color: '#98a2b3' }}>
              Consulta realizada em {new Date().toLocaleString()} via Serasa Experian
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
